import { objectLookup } from "../../utils";
import { RemoteConfigProperties } from "../../models/featureFlags";
import { AgentSessions } from "./sessions";
import { isAgentSSEError } from "../../models/aiAgents";
import { isAgentProductContext, AVAILABLE_AI_AGENTS } from "./types";
import { handleAgentSessionRequestError, AgentSessionServerError, isAuthTokenExpiredError, isAuthTokenExpiredResponse } from "./errors";
import { AGENT_CONFIGS, agentSlug } from "./agents.config";
import { EventStreamContentType, fetchEventSource } from "@microsoft/fetch-event-source";
export * from "./sessions";
export * from "./sessionIds";
export * from "./sessions.lists";
/**
 * {@include ./README.md}
 */
export class AiAgents {
    constructor(sdk) {
        this.sdk = sdk;
        this.agents = AVAILABLE_AI_AGENTS.reduce((acc, agent) => {
            acc[agent] = new AgentSessions(sdk, agent);
            return acc;
        }, {});
    }
    /**
     * Get a streaming response from the agent session using Server-Sent Events.
     * Uses @microsoft/fetch-event-source for cross-platform SSE support with POST.
     * @throws {UnauthenticatedError} If the user is not authenticated.
     * @throws {AgentSessionNotFoundError} If the session ID is not found (404).
     */
    async runSSE({ agent, sessionId, message, context, signal }, callbacks = {}) {
        const { message: text, attachments } = await this.formatMessageForAgent(agent, {
            sessionId,
            message,
            attachments: context?.attachments
        });
        const request = {
            agent,
            sessionId,
            message: text,
            context: { ...(context || {}), attachments },
            signal
        };
        return this.isLegacyEndpoint(agent)
            ? this.runSSELegacy(this.sdk, request, callbacks)
            : this.streamQuery(request, callbacks);
    }
    runSSELegacy(sdk, { agent, sessionId, message, context, signal }, { onEvent, onError, onComplete }) {
        const url = `${sdk.urls.baseAiAgentsUrlLegacy}/${agent}/run/sse`;
        const headers = sdk.ai.getAgentRequestHeader(agent, { context });
        const body = JSON.stringify({ sessionId, message });
        const reconcileCache = () => {
            sdk.ai.agents[agent].reconcileCache("delete", { id: sessionId });
        };
        // Conversions are async; dispatch them in order and drain before completing (see
        // createOrderedDispatcher).
        const dispatcher = this.createOrderedDispatcher(agent, { onEvent, onError }, "Failed to parse SSE event");
        return new Promise((resolve, reject) => {
            fetchEventSource(url, {
                method: "POST",
                headers,
                body,
                signal,
                // Keep the stream open when the tab is hidden
                openWhenHidden: true,
                async onopen(response) {
                    if (response.ok &&
                        response.headers.get("content-type")?.includes(EventStreamContentType)) {
                        // We want to clear the cache for the session when successfully establishing the SSE connection to ensure any
                        // subsequent calls to fetch the session will get the updated data with the new messages and responses from the agent.
                        reconcileCache();
                        return; // Connection established successfully
                    }
                    handleAgentSessionRequestError(response, {
                        id: sessionId,
                        defaultError: new AgentSessionServerError()
                    });
                },
                onmessage: (ev) => {
                    if (!ev.data)
                        return;
                    try {
                        const parsed = JSON.parse(ev.data);
                        dispatcher.dispatch(parsed);
                    }
                    catch {
                        console.warn("Failed to parse SSE event:", ev.data);
                    }
                },
                onclose() {
                    // Drain the ordered conversions before completing so the last onEvent isn't lost.
                    dispatcher.settled().then(() => {
                        onComplete?.();
                        resolve();
                    });
                },
                onerror(err) {
                    // fetch-event-source auto-retries unless onerror throws (returning
                    // schedules a retry). Rethrow so a fatal error aborts instead of
                    // re-POSTing and resubmitting the prompt.
                    throw err;
                }
            }).catch(reject);
        });
    }
    /**
     * ADK requests go through a `{class_method, input}` envelope to
     * `${baseAiAgentsUrl}/${slug}/:streamQuery?alt=sse` — the only endpoint that actually runs the agent.
     *
     * The real Vertex AI Agent Engine wire format here is NDJSON (newline-delimited JSON objects)
     * over a chunked `application/json` response, confirmed by calling the Agent Engine resource
     * directly. The `alt=sse` name and the possibility of a proxy reframing the stream mean we parse
     * defensively: each line may be bare JSON or `data:`-prefixed SSE framing, and SSE control lines
     * are skipped. So this reads the response body as a stream rather than using an SSE client like
     * `@microsoft/fetch-event-source`.
     */
    async streamQuery({ agent, sessionId, message, context, signal }, { onEvent, onError, onComplete }) {
        const url = `${this.sdk.urls.baseAiAgentsUrl}/${agentSlug(agent)}/:streamQuery?alt=sse`;
        const body = JSON.stringify({
            class_method: "async_stream_query",
            input: { user_id: this.sdk.getCurrentUser()?.id, session_id: sessionId, message }
        });
        const sendRequest = () => fetch(url, {
            method: "POST",
            headers: this.getAgentRequestHeader(agent, { context }),
            body,
            signal
        });
        let response = await sendRequest();
        // A stale ID token comes back as a 401 AUTH_TOKEN_EXPIRED; force a fresh token and retry once.
        if (await isAuthTokenExpiredResponse(response)) {
            await this.sdk.madFire.refreshFirebaseIdToken();
            response = await sendRequest();
        }
        if (!response.ok) {
            handleAgentSessionRequestError(response, {
                id: sessionId,
                defaultError: new AgentSessionServerError()
            });
        }
        this.agents[agent].reconcileCache("delete", { id: sessionId });
        // Conversions are async; dispatch them in order and drain before completing (see
        // createOrderedDispatcher).
        const dispatcher = this.createOrderedDispatcher(agent, { onEvent, onError }, "Failed to parse agent response");
        // Only surface one parse failure per stream so a reframed/garbled response fails loudly once
        // rather than hanging silently or spamming onError for every bad line.
        let parseErrorReported = false;
        const processLine = (line) => {
            const json = stripStreamLineFraming(line);
            if (!json)
                return;
            let parsed;
            try {
                parsed = JSON.parse(json);
            }
            catch {
                if (!parseErrorReported) {
                    parseErrorReported = true;
                    dispatcher.dispatch({ error: "Failed to parse streamed agent response" });
                }
                return;
            }
            dispatcher.dispatch(parsed);
        };
        const reader = response.body?.getReader();
        if (!reader) {
            onComplete?.();
            return;
        }
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
            const { done, value } = await reader.read();
            if (done)
                break;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop() ?? "";
            lines.forEach(processLine);
        }
        if (buffer) {
            processLine(buffer);
        }
        await dispatcher.settled();
        onComplete?.();
    }
    /**
     * Builds an ordered dispatcher for streamed events. Conversions (`events.fromService`) are async and
     * of varying duration, so firing them detached would let events reach `onEvent` out of order and let
     * the read loop finish before the last one is delivered. Each item is chained onto the previous so
     * delivery preserves stream order, and `settled()` resolves once every dispatched item has been
     * delivered — await it before invoking `onComplete`.
     */
    createOrderedDispatcher(agent, { onEvent, onError }, conversionErrorLabel) {
        let queue = Promise.resolve();
        const dispatch = (parsed) => {
            if (isAgentSSEError(parsed)) {
                queue = queue.then(() => {
                    onError?.(parsed);
                });
                return;
            }
            queue = queue.then(async () => {
                try {
                    const event = await this.agents[agent].events.fromService(parsed);
                    onEvent?.(event);
                }
                catch (err) {
                    onError?.({
                        error: `${conversionErrorLabel}${err instanceof Error && err.message ? `: ${err.message}` : ""}`
                    });
                }
            });
        };
        return { dispatch, settled: () => queue };
    }
    async formatMessageForAgent(agent, { sessionId, message, attachments }) {
        const formatFn = objectLookup(AGENT_CONFIGS, `${agent}.formatMessage`) ||
            ((val) => val);
        return formatFn({ sessionId, message, attachments }, this.sdk);
    }
    /**
     * Agent endpoints requests require an Authorization header with the user's Firebase custom auth token.
     * Since the typical auth token is a mad-auth token that get's set in {@link MadSDK.authentication} module constructor,
     * we need to override the default axios request Authorization header with the Firebase custom auth token for all requests to the AI Agents endpoints.
     * This method returns the config object for axios requests to include the header.
     */
    axiosRequestConfig(agent, { context, signal, legacy } = {}) {
        return {
            signal,
            transformRequest: [
                (data, headers = {}) => {
                    // Override happens here, after interceptor runs
                    Object.assign(headers, this.getAgentRequestHeader(agent, { context, legacy }));
                    if (typeof data === "object") {
                        // since we're overriding the request, we need to ensure that the data is stringified before sending it to the server,
                        // this is typically handled by axios, but since we're overriding the request, we need to do it manually here.
                        return JSON.stringify(data);
                    }
                    return data;
                }
            ]
        };
    }
    /**
     * Runs an ID-token-authenticated (v2/ADK) agent request and, on a 401 AUTH_TOKEN_EXPIRED,
     * force-refreshes the Firebase ID token and retries once. Legacy requests use the custom
     * token, so they skip the refresh and rethrow.
     */
    async withIdTokenRetry(agent, op) {
        try {
            return await op();
        }
        catch (err) {
            if (this.isLegacyEndpoint(agent) || !isAuthTokenExpiredError(err)) {
                throw err;
            }
            await this.sdk.madFire.refreshFirebaseIdToken();
            return op();
        }
    }
    /**
     * Get auth headers for fetch() requests.
     * Agent endpoints require an Authorization header with the user's Firebase custom auth token.
     * Which is different from the typical mad-auth token used in axios requests.
     * @param legacy Forces legacy (custom-token) auth regardless of the agent's ADK migration status.
     * Needed by callers (like signed-url uploads) whose endpoint is unconditionally legacy.
     */
    getAgentRequestHeader(agent, { context: { product, attachments } = {}, legacy = this.isLegacyEndpoint(agent) } = {}) {
        // ADK endpoints verify a real Firebase ID token server-side; legacy uses the custom
        // token minted for signInWithCustomToken, which isn't independently verifiable.
        const token = legacy
            ? this.sdk.authentication.getCurrentAccount()?.firebaseCustomAuthToken
            : this.sdk.madFire.firebaseIdToken;
        const header = {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
        };
        if (isAgentProductContext(product)) {
            header["X-Product-Context"] = product;
        }
        // TODO: Support multiple attachments in the future if needed,
        // currently BE only supports a single attachment.
        const fileUri = attachments?.[0]?.fileUri;
        if (fileUri) {
            header["X-File-URI"] = fileUri;
        }
        return header;
    }
    /**
     * Check if the given agent is still on the legacy (v1) endpoint, i.e. the backend ADK migration hasn't happened for it yet.
     */
    isLegacyEndpoint(agent) {
        if (agent === "info") {
            return !this.sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.INFO_AGENT_V2_ENDPOINT);
        }
        return true;
    }
}
/**
 * Extracts the JSON payload from a single streamed line. Handles both bare NDJSON and `data:`-prefixed
 * SSE framing (in case a proxy reframes the stream), and returns an empty string for blank lines,
 * SSE comments (`:`), and SSE control lines (`event:`/`id:`/`retry:`) that carry no JSON payload.
 */
function stripStreamLineFraming(line) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith(":"))
        return "";
    if (trimmed.startsWith("data:")) {
        return trimmed.slice("data:".length).trim();
    }
    if (trimmed.startsWith("event:") || trimmed.startsWith("id:") || trimmed.startsWith("retry:")) {
        return "";
    }
    return trimmed;
}
//# sourceMappingURL=index.js.map